import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type RedirectRequest,
} from '@azure/msal-browser';
import { env } from './env';

/**
 * MSAL configuration for Microsoft Entra ID (agency-user sign-in).
 * Only constructed/used when dev-auth is OFF (i.e. a real SPA app registration
 * exists). See `devAuthEnabled` in env.ts.
 *
 * Built with the instance, never when this module loads: it reads `window`, and a module that
 * imports the auth context (a post's creator avatar, for one) must still load where there is no
 * DOM, as the server-rendered specs do.
 */
/**
 * Hosts MSAL is allowed to treat as an authority.
 *
 * MSAL validates an authority against a Microsoft-published list and REFUSES anything not on it —
 * which is every External ID (CIAM) tenant, since those sign in at `<tenant>.ciamlogin.com`. The
 * host has to be declared for such an install to work at all. Derived from the configured
 * authority rather than hardcoded, so the declaration can never drift from what is actually used;
 * an unparseable value yields none, leaving MSAL's own validation to reject it.
 */
function knownAuthorities(): string[] {
  try {
    return [new URL(env.entraAuthority).host];
  } catch {
    return [];
  }
}

function configuration(): Configuration {
  return {
    auth: {
      clientId: env.entraSpaClientId,
      authority: env.entraAuthority,
      knownAuthorities: knownAuthorities(),
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // storeAuthStateInCookie defaults to false; omitted so the config type-checks
      // across @azure/msal-browser majors (the property's typing changed in v5).
      cacheLocation: 'localStorage',
    },
  };
}

/**
 * Scopes requested when acquiring an API token. `offline_access` is explicit so a
 * refresh token is always minted — that's what lets acquireTokenSilent transparently
 * renew an expired access token after the tab has been idle (no re-login needed).
 */
export const loginRequest: RedirectRequest = {
  scopes: env.entraApiScope ? [env.entraApiScope, 'offline_access'] : ['offline_access'],
};

let instance: PublicClientApplication | null = null;

/** Lazily create the MSAL instance (avoid constructing it in dev-auth mode). */
export function getMsalInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication(configuration());
  }
  return instance;
}

/**
 * Is this cached account one THIS install can get a token for?
 *
 * MSAL's account cache is per ORIGIN, not per client id: account keys are `msal.<n>.account.keys`
 * with no client id in them, while token keys are `msal.<n>.token.keys.<clientId>`. So when an
 * install is re-pointed at a different tenant or app registration, every account signed in under
 * the old one stays in `localStorage` and `getAllAccounts()` keeps returning it — with no tokens
 * reachable under the new client id.
 *
 * Matching on `tenantId` rather than `environment`, because the environment of a workforce account
 * is a login host that does not equal the configured authority host (`login.windows.net` vs
 * `login.microsoftonline.com`), while the tenant GUID is exactly what changes when an install is
 * re-pointed.
 *
 * An install that serves no tenant id keeps the old "any account" behaviour, so nothing changes
 * for a deployment that does not serve `/config.js`.
 */
function accountIsForThisInstall(account: AccountInfo): boolean {
  const want = env.entraTenantId.trim().toLowerCase();
  if (!want) return true;
  return account.tenantId?.trim().toLowerCase() === want;
}

/**
 * The account to acquire tokens for, or null when none of the cached ones belongs to this install.
 *
 * Replaces `getAllAccounts()[0]`, which was correct only while no install's client id could ever
 * move. `addAccountKeyToMap` **appends**, so `[0]` is the OLDEST cached account — after a
 * re-point it is the retired one, `acquireTokenSilent` finds no credentials for it under the new
 * client id and throws, the caller treats that as "not signed in", and signing in interactively
 * appends the new account at the end without displacing it. That is a sign-in card that loops for
 * ever, and because the unauthenticated branch renders only that card, nothing that could call
 * `signOut` (the one path that clears the cache) can be reached to escape it.
 *
 * Prefers the ACTIVE account, and sets it when picking one, so the choice survives the next load.
 */
export function pickAccount(msal: PublicClientApplication): AccountInfo | null {
  const active = msal.getActiveAccount();
  if (active && accountIsForThisInstall(active)) return active;
  const match = msal.getAllAccounts().find(accountIsForThisInstall) ?? null;
  // Null is written deliberately when nothing matches: it clears an active account left pointing
  // at the retired tenant, which would otherwise be returned above on every later call.
  msal.setActiveAccount(match);
  return match;
}
